import React, { useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  styled,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import 'react-image-crop/src/ReactCrop.scss';
import ReactCrop, { Crop,PixelCrop } from "react-image-crop";



import { SubmitButton } from "ui/components/shared/SubmitButton";
import { useTranslation } from "react-i18next";
const StyledDialog = styled(Dialog)(() => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem",
    maxWidth: "600px",
    width: "100%",
    maxHeight: "90vh",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
}));

const Container = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "1.5rem 1rem",
  gap: "1.5rem",
}));

const CropWrapper = styled(Box)(() => ({
  width: "100%",
  maxHeight: "60vh",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  background: "#f9f9f9",
  borderRadius: "0.5rem",
  padding: "1rem",
}));

const StyledImage = styled("img")(() => ({
  maxWidth: "100%",
  maxHeight: "60vh",
  objectFit: "contain",
  borderRadius: "0.5rem",
}));

const ButtonRow = styled(Box)(() => ({
  display: "flex",
  gap: "1rem",
  justifyContent: "center",
  flexWrap: "wrap",
  marginTop: "0.5rem",
}));

type Props = {
  file: File;
  onCropConfirm: (blob: Blob) => void;
  onCancel: () => void;
};

const CropImageDialog = ({ file, onCropConfirm, onCancel }: Props) => {
  const [crop, setCrop] = useState<Crop>({
    unit: "%",
    width: 80,
    height: 80,
    x: 10,
    y: 10,
  });

  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const theme = useTheme();
  const { t } = useTranslation();

  const onImageLoad = (img: HTMLImageElement) => {
    imgRef.current = img;
  };

  const handleCrop = () => {
    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement("canvas");
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    canvas.toBlob((blob) => {
      if (blob) onCropConfirm(blob);
    }, "image/jpeg");
  };

  return (
    <StyledDialog open onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={{ px: 3, pt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={600}>
            {t("cropDialog.title")}
          </Typography>
          <IconButton onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Container>
          <Typography
            variant="body2"
            color={theme.palette.text.secondary}
            textAlign="center"
            maxWidth="30rem"
          >
            {t("cropDialog.instruction")}
          </Typography>

          <CropWrapper>
            <ReactCrop
              crop={crop}
              onChange={(c: Crop) => setCrop(c)}
              onComplete={(c: PixelCrop) => setCompletedCrop(c)}
              aspect={1}
              keepSelection
            >
              <StyledImage
                src={URL.createObjectURL(file)}
                alt={t("cropDialog.alt")}
                onLoad={(e) => onImageLoad(e.currentTarget)}
              />
            </ReactCrop>
          </CropWrapper>

          <ButtonRow>
            <SubmitButton color="success" onClick={handleCrop}>
              {t("cropDialog.save")}
            </SubmitButton>
            <SubmitButton color="error" variant="contained" onClick={onCancel}>
              {t("cropDialog.cancel")}
            </SubmitButton>
          </ButtonRow>
        </Container>
      </DialogContent>
    </StyledDialog>
  );
};

export default CropImageDialog;
